// combinator.ts
// Core: layers of choices in, snapshots out. docs/reference/combinator-panel/

export interface Layer {
  id: string;
  options: string[];
}

export interface Outcome {
  when: Record<string, string>;
  state: Record<string, unknown>;
}

export interface Config {
  layers: Layer[];
  outcomes: Outcome[];
}

export type Selection = Record<string, string | null>;

export interface Snapshot {
  selection: Selection;
  available: Record<string, string[]>;
  outcome: Record<string, unknown> | null;
  /** The layer ids whose selection this snapshot changed, in layer order. */
  changed: string[];
}

export type Listener = (snapshot: Snapshot) => void;

export interface Combinator {
  readonly config: Config;
  select(layerId: string, optionId: string): Snapshot;
  snapshot(): Snapshot;
  subscribe(listener: Listener): () => void;
}

/** Whether an outcome agrees with the selection on the first `count` layers. */
function agrees(outcome: Outcome, layers: Layer[], selection: Selection, count: number): boolean {
  for (let at = 0; at < count; at += 1) {
    const id = layers[at].id;

    if (outcome.when[id] !== selection[id]) return false;
  }

  return true;
}

/**
 * The options a layer may offer: those some outcome holds while it agrees
 * with the layers above.
 */
function availableFor(config: Config, selection: Selection, index: number): string[] {
  const layer = config.layers[index];
  const candidates = config.outcomes.filter((outcome) => agrees(outcome, config.layers, selection, index));

  return layer.options.filter((option) => candidates.some((outcome) => outcome.when[layer.id] === option));
}

/**
 * Walk the layers in order. A layer whose selection is not available
 * moves to its first available option, or to null when it has none.
 */
function resolve(config: Config, wanted: Selection): { selection: Selection; available: Record<string, string[]> } {
  const selection: Selection = {};
  const available: Record<string, string[]> = {};

  config.layers.forEach((layer, index) => {
    const options = availableFor(config, selection, index);
    const current = wanted[layer.id] ?? null;

    available[layer.id] = options;
    selection[layer.id] = current !== null && options.includes(current) ? current : options[0] ?? null;
  });

  return { selection, available };
}

/** The first outcome that matches the whole selection, or null. */
function outcomeOf(config: Config, selection: Selection): Record<string, unknown> | null {
  const complete = config.layers.every((layer) => selection[layer.id] !== null);

  if (!complete) return null;

  const match = config.outcomes.find((outcome) => agrees(outcome, config.layers, selection, config.layers.length));

  return match ? match.state : null;
}

export function createCombinator(config: Config, initial: Selection = {}): Combinator {
  const listeners = new Set<Listener>();
  let current = resolve(config, initial);

  const build = (changed: string[]): Snapshot => ({
    selection: { ...current.selection },
    available: { ...current.available },
    outcome: outcomeOf(config, current.selection),
    changed
  });

  return {
    config,

    snapshot(): Snapshot {
      return build([]);
    },

    select(layerId: string, optionId: string): Snapshot {
      const layer = config.layers.find((entry) => entry.id === layerId);
      const known = Boolean(layer) && layer!.options.includes(optionId);
      const offered = known && (current.available[layerId] || []).includes(optionId);

      if (!offered) return build([]);

      const before = current.selection;

      current = resolve(config, { ...before, [layerId]: optionId });

      const changed = config.layers
        .map((entry) => entry.id)
        .filter((id) => before[id] !== current.selection[id]);
      const snapshot = build(changed);

      if (changed.length) listeners.forEach((listener) => listener(snapshot));

      return snapshot;
    },

    subscribe(listener: Listener): () => void {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    }
  };
}
